import React, { useEffect, useState } from "react";
import DojoShell, { RED } from "@/components/DojoShell";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { IoClose, IoLogoInstagram, IoCreateOutline } from "react-icons/io5";
import { listMembers, getPageConfig } from "@/lib/api";

export default function Membres() {
  const nav = useNavigate();
  const { loading, isAuthenticated, user } = useAuth();
  const [members, setMembers] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!loading && !isAuthenticated) nav("/connexion", { replace: true, state: { from: { pathname: "/membres" } } });
  }, [loading, isAuthenticated, nav]);

  useEffect(() => { listMembers().then(setMembers).catch(() => setMembers([])); }, []);
  useEffect(() => { getPageConfig("questionnaire").then((c) => setQuestions(c?.questions || [])).catch(() => {}); }, []);

  const hasAnswers = (m) => questions.some((q) => (m.questionnaire || {})[q.id]);

  return (
    <DojoShell crumbs={[{ label: "Membres" }]}>
      <div style={st.head}>
        <span style={st.headKanji}>人</span>
        <div style={{ flex: 1 }}>
          <h1 style={st.h1}>Les Membres du Dōjō</h1>
          <p style={st.sub}>La communauté des conteurs et conteuses de Tengoku.</p>
        </div>
        <button style={st.editBtn} onClick={() => nav("/profil")} data-testid="membres-edit-profile">
          <IoCreateOutline size={17} /> Mon profil
        </button>
      </div>

      {members === null ? (
        <p style={{ color: "var(--text-secondary)" }}>Chargement…</p>
      ) : members.length === 0 ? (
        <p style={{ color: "var(--text-secondary)" }}>{"Aucun membre pour l'instant. Sois le premier à créer ton profil !"}</p>
      ) : (
        <div style={st.grid} data-testid="membres-grid">
          {members.map((m) => {
            const clickable = hasAnswers(m);
            return (
              <div key={m.id} style={{ ...st.card, cursor: clickable ? "pointer" : "default" }} data-testid={`membre-${m.id}`}
                onClick={() => clickable && setSelected(m)}>
                <div style={st.avatarWrap}>
                  {m.avatar ? <img src={m.avatar} alt={m.name} style={st.avatar} /> : <span style={st.avatarFallback}>{(m.name || "?").charAt(0).toUpperCase()}</span>}
                  {m.role === "admin" && <span style={st.sensei}>Sensei</span>}
                </div>
                <div style={st.name}>{m.name}</div>
                {m.bio && <div style={st.bio}>{m.bio}</div>}
                {m.instagram && (
                  <a href={`https://instagram.com/${m.instagram}`} target="_blank" rel="noreferrer" style={st.ig} onClick={(e) => e.stopPropagation()}>
                    <IoLogoInstagram size={14} style={{ verticalAlign: "-2px", marginRight: 4 }} />@{m.instagram}
                  </a>
                )}
                {clickable && <div style={st.more} data-testid={`membre-more-${m.id}`}>Voir le profil →</div>}
              </div>
            );
          })}
        </div>
      )}

      {selected && (
        <div style={st.overlay} onClick={() => setSelected(null)} data-testid="membre-detail">
          <div style={st.modal} onClick={(e) => e.stopPropagation()}>
            <button style={st.modalX} onClick={() => setSelected(null)} data-testid="membre-detail-close"><IoClose size={22} /></button>
            <div style={st.detailHead}>
              <div style={st.avatarWrapBig}>
                {selected.avatar ? <img src={selected.avatar} alt={selected.name} style={st.avatar} /> : <span style={st.avatarFallback}>{(selected.name || "?").charAt(0).toUpperCase()}</span>}
              </div>
              <div>
                <div style={st.detailName}>{selected.name}{selected.role === "admin" && <span style={st.senseiInline}>Sensei</span>}</div>
                {selected.bio && <div style={st.detailBio}>{selected.bio}</div>}
                {selected.instagram && (
                  <a href={`https://instagram.com/${selected.instagram}`} target="_blank" rel="noreferrer" style={st.ig}>
                    <IoLogoInstagram size={14} style={{ verticalAlign: "-2px", marginRight: 4 }} />@{selected.instagram}
                  </a>
                )}
              </div>
            </div>

            <div style={st.qaList}>
              {questions.map((q) => {
                const a = (selected.questionnaire || {})[q.id];
                if (!a) return null;
                return (
                  <div key={q.id} style={st.qa} data-testid={`membre-qa-${q.id}`}>
                    <div style={st.qaQ}>{q.text}</div>
                    <div style={st.qaA}>{a}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </DojoShell>
  );
}

const st = {
  head: { display: "flex", gap: 16, alignItems: "center", marginBottom: 24, flexWrap: "wrap" },
  headKanji: { fontSize: 56, fontWeight: 800, color: RED, lineHeight: 1 },
  h1: { fontSize: 32, fontWeight: 900, margin: 0 },
  sub: { color: "var(--text-secondary)", fontSize: 15, margin: "6px 0 0" },
  editBtn: { display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 16px", borderRadius: 12, border: "none", background: RED, color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 18 },
  card: { display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: "24px 18px", borderRadius: 18, border: "1px solid var(--border-c)", background: "var(--bg-primary)", boxShadow: "0 8px 22px rgba(0,0,0,0.06)", transition: "transform .15s" },
  avatarWrap: { position: "relative", marginBottom: 14 },
  avatarWrapBig: { position: "relative", width: 84, height: 84, flexShrink: 0 },
  avatar: { width: 84, height: 84, borderRadius: "50%", objectFit: "cover", border: `3px solid color-mix(in srgb, var(--brand) 13%, transparent)` },
  avatarFallback: { width: 84, height: 84, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: "color-mix(in srgb, var(--brand) 10%, transparent)", color: RED, fontSize: 32, fontWeight: 800, border: `3px solid color-mix(in srgb, var(--brand) 13%, transparent)` },
  sensei: { position: "absolute", bottom: -4, left: "50%", transform: "translateX(-50%)", fontSize: 10, fontWeight: 800, color: "#fff", background: RED, padding: "2px 8px", borderRadius: 999, whiteSpace: "nowrap" },
  senseiInline: { fontSize: 11, fontWeight: 800, color: "#fff", background: RED, padding: "2px 8px", borderRadius: 999, marginLeft: 8, verticalAlign: "middle" },
  name: { fontWeight: 800, fontSize: 16, color: "var(--text-primary)" },
  bio: { fontSize: 12.5, color: "var(--text-secondary)", marginTop: 6, lineHeight: 1.5 },
  ig: { fontSize: 13, color: RED, fontWeight: 700, marginTop: 10, textDecoration: "none", display: "inline-block" },
  more: { marginTop: 12, fontSize: 12.5, fontWeight: 800, color: RED },

  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)", zIndex: 6000, display: "flex", alignItems: "center", justifyContent: "center", padding: 18 },
  modal: { position: "relative", width: "100%", maxWidth: 560, maxHeight: "88vh", overflowY: "auto", background: "var(--bg-secondary)", borderRadius: 20, border: "1px solid var(--border-c)", padding: "26px 24px", boxShadow: "0 30px 80px rgba(0,0,0,0.35)" },
  modalX: { position: "absolute", top: 14, right: 14, border: "none", background: "transparent", color: "var(--text-secondary)", cursor: "pointer" },
  detailHead: { display: "flex", gap: 16, alignItems: "center", marginBottom: 20, paddingRight: 24 },
  detailName: { fontSize: 22, fontWeight: 900, color: "var(--text-primary)", fontFamily: "var(--font-display)" },
  detailBio: { fontSize: 14, color: "var(--text-secondary)", marginTop: 4, lineHeight: 1.5 },
  qaList: { display: "flex", flexDirection: "column", gap: 14 },
  qa: { padding: "14px 16px", borderRadius: 14, background: "var(--bg-primary)", border: "1px solid var(--border-c)" },
  qaQ: { fontSize: 13, fontWeight: 800, color: RED, marginBottom: 6 },
  qaA: { fontSize: 14.5, color: "var(--text-primary)", lineHeight: 1.55, whiteSpace: "pre-wrap" },
};
